import React, { useEffect, useMemo, useState } from 'react';
import '../styles/team-movements.scss';
import { TeamWallChart } from '@shared/ui/organisms/movements-wallchart/TeamWallChart';
import WallChartHeader from '@shared/ui/organisms/movements-wallchart/components/WallChartHeader/WallChartHeader';
import DeskAvailability from '@shared/ui/organisms/movements-wallchart/components/desk-availability/DeskAvailability';
import { TeamWithMovements } from '@features/whereabouts/models/movements.models';
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentUser } from "@shared/store/auth.duck";
import { CurrentUsersWeeklyWhereabouts } from '@shared/ui/organisms/CurrentUsersWeeklyWhereabouts';
import { selectAllTags } from "@shared/store/userTags.duck";
import TagMovementsWallChart from '@shared/ui/organisms/movements-wallchart/TagMovementsWallChart';
import { TeamMultiSelectWithFavourites } from '@shared/ui/organisms/team-drop-down-list/TeamMultiSelectWithFavourites';
import { selectAllTeams } from "@shared/store/config.duck";
import { selectWhereabouts } from "../models/team-movements.duck";
import { LineReportsWallChart } from '@shared/ui/organisms/movements-wallchart/LineReportsWallChart';
import { TeamEntity } from '@shared/models/team.models';
import { Tag } from '@shared/models/tags.models';
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { setWhereaboutsLineReportSelected } from "../../../store/LineReportsWhereabouts.duck";
import { Department } from "../../departments/data/departments.models";
import { setFavouritedUsersWhereaboutsSelected } from "../../../store/FavouritedUsersWhereabouts.duck";
import { FavouritedUsersWallChart } from '@shared/ui/organisms/movements-wallchart/FavouritedUsersWallChart';
import { useWhereaboutsScope } from '@features/whereabouts/pages/whereabouts-v2/WhereaboutsScopeContext';
import { useScopedWeeklyWhereaboutsLoader } from '@features/whereabouts/hooks/useScopedWeeklyWhereaboutsLoader';

export default function TeamMovementsList(props: Props) {
  const dispatch = useDispatch();
  const { scope, isScoped } = useWhereaboutsScope();
  const teams = useSelector(selectAllTeams);
  const workGroups = useSelector(selectAllTags);
  const currentUser = useSelector(selectCurrentUser);
  const whereaboutsGroups = useSelector(selectWhereabouts);
  const [lineReportsSelected, setLineReportsSelected] = useState(false);
  const [favouriteUsersSelected, setFavouriteUsersSelected] = useState(false);
  const hasLineReportsEnabled = !!currentUser?.companyEntity?.lineManagementEnabled;

  const updatedTeams = useMemo(() => {
    return teams.map((t) => t.id === currentUser?.teamEntity?.id ? ({...t, name: `My team (${t.name})` }) : t);
  }, [teams, currentUser]);

  useScopedWeeklyWhereaboutsLoader(scope);

  useEffect(() => {
    setLineReportsSelected(window.localStorage.getItem('lineReportsSelected') === 'true');
    setFavouriteUsersSelected(window.localStorage.getItem('favouritesSelected') === 'true');
  }, []);

  const onSelect = (team: TeamEntity[], workGroups: Tag[], offices: OfficeEntity[], departments: Department[], showLineReports?: boolean, showFavourites?: boolean) => {
    setLineReportsSelected(!!showLineReports);
    window.localStorage.setItem('lineReportsSelected', (!!showLineReports).toString())
    setFavouriteUsersSelected(!!showFavourites);
    window.localStorage.setItem('favouritesSelected', (!!showFavourites).toString())
  }

  // The line-reports and favourites pseudo groups belong to the followed view
  // only; a page pinned to one team/workgroup never shows them.
  const showLineReports = !isScoped && lineReportsSelected;
  const showFavourites = !isScoped && favouriteUsersSelected;

  useEffect(() => {
    dispatch(setWhereaboutsLineReportSelected(showLineReports));
  }, [dispatch, showLineReports]);

  useEffect(() => {
    dispatch(setFavouritedUsersWhereaboutsSelected(showFavourites));
  }, [dispatch, showFavourites]);

  return (
    <div className="teamMovements__content">
      {!props.hideDayHeaders && <WallChartHeader hideDateFlicker={props.hideDateFlicker} />}
      <DeskAvailability className="teamMovements__deskAvailabilityRow--desktop"/>
      <CurrentUsersWeeklyWhereabouts />
      {!isScoped && (
        <div style={{maxWidth: '100%', minWidth: 350, marginTop: 24, marginBottom: 24}}>
          <TeamMultiSelectWithFavourites teams={updatedTeams}
                                         workGroups={workGroups}
                                         includeLineReportOption={hasLineReportsEnabled}
                                         isLineReportOptionSelected={lineReportsSelected}
                                         isFavouritedUsersOptionSelected={favouriteUsersSelected}
                                         onSelect={onSelect}
                                         reloadTeamWhereaboutsOnSelect={true} />
        </div>
      )}
      {isScoped && <div style={{marginTop: 24}} />}

      {showFavourites && <FavouritedUsersWallChart enabledSetMovements={true} excludeCurrentUser={true} />}
      {showLineReports && <LineReportsWallChart enabledSetMovements={true} excludeCurrentUser={true} />}

      {whereaboutsGroups.teams.map((teamMovements, key) => (
        <TeamWallChart key={key}
                       movementsViewObject={teamMovements}
                       disableAccordian={true}
                       showHeader={true}
                       showFooter={true}
                       excludeCurrentUser={true}
                       hasWeekNotesEnabled={currentUser?.companyEntity.weeklyNotesEnabled}
                       enabledSetMovements={true}/>
      ))}
      {whereaboutsGroups.workgroups.map((tagMovements, key) => (
        <TagMovementsWallChart key={key}
                               tagWithMovements={tagMovements}
                               showHeader={true}
                               showFooter={true}
                               excludeCurrentUser={true} />
      ))}
    </div>
  )
}

interface Props {
  teamMovements?: TeamWithMovements;
  hideDateFlicker?: boolean;
  hideDayHeaders?: boolean;
}
